import { PropsWithChildren, useEffect, useRef } from 'react'
import { SquircleAdapter } from '@/components/SquircleAdapter'

import './styles.scss'
import { useCakeMakerState } from '@/store/useCakeMaker'

const GAP = 2

const getItemsInLine = (items: Element[], lineTop: number) => {
  return items.filter((item) => item.getBoundingClientRect().top === lineTop)
}

const getLineWidth = (items: Element[]) => {
  const itemsWidth = items.reduce(
    (width, item) => width + item.getBoundingClientRect().width,
    0,
  )
  const gapsWidth = (items.length - 1) * GAP
  return itemsWidth + gapsWidth
}

type OptionsWrapperProps = PropsWithChildren<{
  'aria-labelledby'?: string
}>

export function OptionsWrapper({
  children,
  'aria-labelledby': ariaLabelledBy,
}: OptionsWrapperProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { candies } = useCakeMakerState()

  useEffect(() => {
    const calculateWidth = () => {
      const wrapper = wrapperRef.current
      if (!wrapper) return

      wrapper.style.width = 'fit-content'

      const items = Array.from(wrapper.children)
      const width = Math.max(
        ...items.reduce((acc: number[], item) => {
          const lineTop = item.getBoundingClientRect().top
          const lineItems = getItemsInLine(items, lineTop)
          const lineWidth = getLineWidth(lineItems)
          return [...acc, lineWidth]
        }, []),
      )

      wrapper.style.width = `${width + 8}px`
    }

    calculateWidth()

    window.addEventListener('resize', calculateWidth)

    return () => {
      window.removeEventListener('resize', calculateWidth)
    }
  }, [candies])

  return (
    <SquircleAdapter
      cornerRadius={12}
      className="options-wrapper"
      ref={wrapperRef}
      role="group"
      aria-labelledby={ariaLabelledBy}
    >
      {children}
    </SquircleAdapter>
  )
}
